import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ArrowLeft, ChevronRight, FileText, Truck } from "lucide-react";

const first = (...vals) => vals.find(v => v !== undefined && v !== null && String(v).trim() !== "");

export default function UnresolvedActionsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedKind, setSelectedKind] = useState(null); // unresolved-summary-card-filter-20260922

  const load = () => {
    setLoading(true); setError("");
    api.get("/pending-actions")
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Could not load unresolved issues"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const quotes = data?.overdue_quotations || [];
  const pcs = data?.unattached_pcs || [];
  const chassis = data?.unattached_chassis || []; // unresolved-chassis-parity-20260922
  const total = quotes.length + pcs.length + chassis.length;

  const issues = useMemo(() => [
    ...quotes.map((q, i) => ({
      key: `quote-${first(q.id,q._id,q.quote_number,q.serial_no,i)}`,
      kind: "quote",
      title: `Overdue quotation ${first(q.quote_number,q.serial_no,q.no,q.number,"")}`.trim(),
      customer: first(q.customer_name,q.customer,q.name,"Customer not specified"),
      detail: first(q.quote_date,q.date,q.created_at,q.status,"Needs follow-up"),
      to: "/quotes",
      action: "Open quotations"
    })),
    ...pcs.map((p, i) => ({
      key: `pc-${first(p.id,p._id,p.pc_number,i)}`,
      kind: "pc",
      title: `PC ${first(p.pc_number,p.pc_number_display,p.number,"")} awaiting vehicle`.trim(),
      customer: first(p.customer_name,p.customer,p.name,"Customer not specified"),
      detail: first(p.planned_vehicle_name,p.vehicle_model,p.items?.[0]?.vehicle_name,"Vehicle/chassis still unresolved"),
      to: "/pcs",
      action: "Attach vehicle"
    })),
    ...chassis.map((c, i) => ({
      key: `chassis-${first(c.id,c._id,c.chassis_last4,i)}`,
      kind: "chassis",
      title: `Chassis ${first(c.chassis_last4,"—")} awaiting PC`,
      customer: first(c.vehicle_name_snapshot,c.custom_vehicle_name,"Vehicle not specified"),
      detail: first(c.date_received,c.created_at,"Available on plant"),
      to: "/chassis-on-plant",
      action: "Match PC"
    }))
  ], [quotes, pcs, chassis]);
  const visibleIssues = selectedKind ? issues.filter(issue => issue.kind === selectedKind) : issues;

  return <div className="max-w-3xl mx-auto pb-20" data-testid="unresolved-actions-page">
    <div className="flex items-start justify-between gap-4">
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 mb-2"><ArrowLeft className="w-3.5 h-3.5"/>Home</Link>
        <h1 className="text-2xl font-semibold text-slate-900">Unresolved Issues</h1>
        <p className="text-sm text-slate-500 mt-1">Choose the issue you want to solve first. This list updates as issues are resolved.</p>
      </div>
      {!loading && <div className="shrink-0 min-w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center font-bold text-amber-800">{total}</div>}
    </div>

    {loading && <div className="mt-6 fc-card p-8 text-center text-sm text-slate-500">Loading unresolved issues…</div>}
    {!loading && error && <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"><div>{error}</div><button onClick={load} className="mt-3 h-9 px-4 rounded-lg bg-white border border-red-200 font-medium">Try again</button></div>}
    {!loading && !error && total === 0 && <div className="mt-6 fc-card p-8 text-center"><div className="text-base font-semibold text-slate-900">All clear</div><div className="text-sm text-slate-500 mt-1">There are no unresolved issues right now.</div></div>}

    {!loading && !error && total > 0 && <>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button type="button" aria-pressed={selectedKind==="quote"} onClick={()=>setSelectedKind(current=>current==="quote"?null:"quote")} className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${selectedKind==="quote"?"ring-2 ring-amber-500":""}`}><div className="text-2xl font-bold text-amber-900">{quotes.length}</div><div className="text-xs text-amber-700 mt-0.5">Overdue quotations</div></button>
        <button type="button" aria-pressed={selectedKind==="pc"} onClick={()=>setSelectedKind(current=>current==="pc"?null:"pc")} className={`rounded-2xl border border-blue-200 bg-blue-50 p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${selectedKind==="pc"?"ring-2 ring-blue-500":""}`}><div className="text-2xl font-bold text-blue-900">{pcs.length}</div><div className="text-xs text-blue-700 mt-0.5">PCs awaiting vehicle</div></button>
        <button type="button" aria-pressed={selectedKind==="chassis"} onClick={()=>setSelectedKind(current=>current==="chassis"?null:"chassis")} className={`rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${selectedKind==="chassis"?"ring-2 ring-indigo-500":""}`}><div className="text-2xl font-bold text-indigo-900">{chassis.length}</div><div className="text-xs text-indigo-700 mt-0.5">Chassis awaiting PC</div></button>
      </div>

      <div className="mt-5 space-y-3">
        {visibleIssues.length===0&&<div className="fc-card p-5 text-sm text-slate-500">No pending items in this category.</div>}
        {visibleIssues.map(issue => <Link key={issue.key} to={issue.to} className="block bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-300 hover:shadow-sm transition-all" data-testid="unresolved-issue-row">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${issue.kind === "quote" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
              {issue.kind === "quote" ? <FileText className="w-5 h-5"/> : <Truck className="w-5 h-5"/>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{issue.title}</div>
              <div className="text-xs text-slate-600 mt-0.5 truncate">{issue.customer}</div>
              <div className="text-[11px] text-slate-400 mt-1 truncate">{issue.detail}</div>
            </div>
            <div className="shrink-0 flex items-center gap-1 text-xs font-semibold text-blue-600">{issue.action}<ChevronRight className="w-4 h-4"/></div>
          </div>
        </Link>)}
      </div>
    </>}
  </div>;
}
